// Sustituto de 'firebase/firestore' que habla con la API propia (/api/fs/*).
// Mantiene la misma forma de uso que el SDK modular de Firebase.

import { apiCall, encode, decode, Timestamp, FieldValueSentinel, GeoPoint, FirebaseError } from './wire.js';

export { Timestamp, GeoPoint };

const AUTO_ID_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
function autoId() {
  const bytes = new Uint8Array(20);
  crypto.getRandomValues(bytes);
  let id = '';
  for (let i = 0; i < 20; i++) id += AUTO_ID_CHARS[bytes[i] % AUTO_ID_CHARS.length];
  return id;
}

const dbInstance = { type: 'firestore', app: null, toJSON: () => ({}) };
export function getFirestore() { return dbInstance; }
export const initializeFirestore = getFirestore;
export function connectFirestoreEmulator() {}
export async function enableIndexedDbPersistence() {}
export async function enableMultiTabIndexedDbPersistence() {}
export function persistentLocalCache() { return {}; }
export function persistentMultipleTabManager() { return {}; }
export function memoryLocalCache() { return {}; }
export async function terminate() {}
export async function clearIndexedDbPersistence() {}
export async function waitForPendingWrites() {}
export async function disableNetwork() {}
export async function enableNetwork() {}

const joinPath = (...parts) => parts.flatMap(p => String(p).split('/')).filter(Boolean).join('/');

// ---------- Referencias ----------
class DocumentReference {
  constructor(path) {
    const segs = path.split('/');
    if (segs.length % 2 !== 0 || segs.some(s => !s)) {
      throw new FirebaseError('invalid-argument', `Ruta de documento no válida: ${path}`);
    }
    this.type = 'document';
    this.firestore = dbInstance;
    this.path = path;
    this.id = segs[segs.length - 1];
    this.converter = null;
  }
  get parent() { return new CollectionReference(this.path.split('/').slice(0, -1).join('/')); }
  withConverter(c) { const r = new DocumentReference(this.path); r.converter = c; return r; }
  toJSON() { return { path: this.path }; }
}

class Query {
  constructor(collectionPath, spec = {}) {
    this.type = 'query';
    this.firestore = dbInstance;
    this._path = collectionPath;
    this._spec = { filters: [], orderBy: [], ...spec };
    this.converter = null;
  }
  withConverter(c) { const q = new Query(this._path, this._spec); q.converter = c; return q; }
}

class CollectionReference extends Query {
  constructor(path) {
    super(path);
    const segs = path.split('/');
    if (segs.length % 2 !== 1 || segs.some(s => !s)) {
      throw new FirebaseError('invalid-argument', `Ruta de colección no válida: ${path}`);
    }
    this.type = 'collection';
    this.path = path;
    this.id = segs[segs.length - 1];
  }
  get parent() {
    const segs = this.path.split('/');
    return segs.length > 1 ? new DocumentReference(segs.slice(0, -1).join('/')) : null;
  }
  withConverter(c) { const r = new CollectionReference(this.path); r.converter = c; return r; }
}

const makeRef = (p) => new DocumentReference(p);

export function collection(parent, path, ...more) {
  const base = parent && parent.type === 'document' ? parent.path : '';
  return new CollectionReference(joinPath(base, path, ...more));
}

export function doc(parent, path, ...more) {
  if (parent && parent.type === 'collection') {
    return new DocumentReference(joinPath(parent.path, path === undefined ? autoId() : path, ...more));
  }
  const base = parent && parent.type === 'document' ? parent.path : '';
  if (path === undefined) throw new FirebaseError('invalid-argument', 'doc() necesita una ruta');
  return new DocumentReference(joinPath(base, path, ...more));
}

export function collectionGroup() {
  throw new FirebaseError('unimplemented', 'collectionGroup no está soportado');
}

export function refEqual(a, b) { return a?.path === b?.path && a?.type === b?.type; }
export function queryEqual(a, b) { return JSON.stringify(a?._spec) === JSON.stringify(b?._spec) && a?._path === b?._path; }

// ---------- Restricciones de consulta ----------
const DOC_ID = { __documentId: true };
export function documentId() { return DOC_ID; }
export class FieldPath {
  constructor(...segments) { this.segments = segments; }
  isEqual(o) { return o instanceof FieldPath && o.segments.join('.') === this.segments.join('.'); }
}
const fieldName = (f) => (f === DOC_ID ? '__name__' : f instanceof FieldPath ? f.segments.join('.') : String(f));

const constraint = (kind, apply) => ({ type: kind, _apply: apply });

export function where(field, op, value) {
  const f = { field: fieldName(field), op, value };
  return Object.assign(constraint('where', (s) => ({ filters: [...s.filters, f] })), { _filter: f });
}
export function or(...cs) {
  const f = { composite: 'or', filters: cs.map(c => c._filter) };
  return Object.assign(constraint('or', (s) => ({ filters: [...s.filters, f] })), { _filter: f });
}
export function and(...cs) {
  const f = { composite: 'and', filters: cs.map(c => c._filter) };
  return Object.assign(constraint('and', (s) => ({ filters: [...s.filters, f] })), { _filter: f });
}
export function orderBy(field, dir = 'asc') {
  return constraint('orderBy', (s) => ({ orderBy: [...s.orderBy, { field: fieldName(field), dir: dir === 'desc' ? 'desc' : 'asc' }] }));
}
export function limit(n) { return constraint('limit', () => ({ limit: n, limitToLast: undefined })); }
export function limitToLast(n) { return constraint('limitToLast', () => ({ limitToLast: n, limit: undefined })); }

function cursor(kind, args, inclusive) {
  return constraint(kind, (s) => {
    let values = args;
    if (args.length === 1 && args[0] instanceof DocumentSnapshot) {
      const snap = args[0];
      values = (s.orderBy.length ? s.orderBy : [{ field: '__name__' }])
        .map(o => (o.field === '__name__' ? snap.id : snap.get(o.field)));
    }
    const key = kind.startsWith('start') ? 'startAt' : 'endAt';
    return { [key]: { values, inclusive } };
  });
}
export const startAt = (...a) => cursor('startAt', a, true);
export const startAfter = (...a) => cursor('startAfter', a, false);
export const endAt = (...a) => cursor('endAt', a, true);
export const endBefore = (...a) => cursor('endBefore', a, false);

export function query(base, ...constraints) {
  let spec = { ...base._spec };
  for (const c of constraints.flat()) if (c && c._apply) spec = { ...spec, ...c._apply(spec) };
  const q = new Query(base._path, spec);
  q.converter = base.converter;
  return q;
}

// ---------- Snapshots ----------
class DocumentSnapshot {
  constructor(ref, wire) {
    this.ref = ref;
    this.id = ref.id;
    this._data = wire && wire.exists !== false && wire.data !== undefined ? decode(wire.data, makeRef) : undefined;
    this._version = wire?.version || 0;
    this.metadata = { hasPendingWrites: false, fromCache: false, isEqual: () => true };
    this._converter = ref.converter;
  }
  exists() { return this._data !== undefined; }
  data() {
    if (this._data === undefined) return undefined;
    if (this._converter?.fromFirestore) return this._converter.fromFirestore(this, {});
    return cloneData(this._data);
  }
  get(field) {
    const segs = fieldName(field).split('.');
    let cur = this._data;
    for (const s of segs) { if (cur == null || typeof cur !== 'object') return undefined; cur = cur[s]; }
    return cur;
  }
}
class QueryDocumentSnapshot extends DocumentSnapshot {}

function cloneData(d) {
  if (Array.isArray(d)) return d.map(cloneData);
  if (d && typeof d === 'object' && d.constructor === Object) {
    const o = {};
    for (const [k, v] of Object.entries(d)) o[k] = cloneData(v);
    return o;
  }
  return d;
}

class QuerySnapshot {
  constructor(q, docs, prev = null) {
    this.query = q;
    this.docs = docs;
    this.size = docs.length;
    this.empty = docs.length === 0;
    this.metadata = { hasPendingWrites: false, fromCache: false };
    this._prev = prev;
  }
  forEach(cb, thisArg) { this.docs.forEach(cb, thisArg); }
  docChanges() {
    if (!this._prev) return this.docs.map((doc, i) => ({ type: 'added', doc, oldIndex: -1, newIndex: i }));
    const before = new Map(this._prev.map((d, i) => [d.id, { d, i }]));
    const out = [];
    this.docs.forEach((doc, i) => {
      const old = before.get(doc.id);
      if (!old) out.push({ type: 'added', doc, oldIndex: -1, newIndex: i });
      else if (old.d._version !== doc._version) out.push({ type: 'modified', doc, oldIndex: old.i, newIndex: i });
      before.delete(doc.id);
    });
    for (const { d, i } of before.values()) out.push({ type: 'removed', doc: d, oldIndex: i, newIndex: -1 });
    return out;
  }
}

// ---------- Lecturas ----------
export async function getDoc(ref) {
  const wire = await apiCall('/fs/get', { path: ref.path });
  return new DocumentSnapshot(ref, wire);
}
export const getDocFromServer = getDoc;
export const getDocFromCache = getDoc;

export async function getDocs(q) {
  const res = await apiCall('/fs/query', { query: encode({ collection: q._path, ...q._spec }) });
  const docs = res.docs.map(w => {
    const r = new DocumentReference(w.path);
    r.converter = q.converter;
    return new QueryDocumentSnapshot(r, w);
  });
  return new QuerySnapshot(q, docs);
}
export const getDocsFromServer = getDocs;
export const getDocsFromCache = getDocs;

export async function getCountFromServer(q) {
  const s = await getDocs(q);
  return { data: () => ({ count: s.size }) };
}
export const getAggregateFromServer = async (q) => {
  const s = await getDocs(q);
  return { data: () => ({ count: s.size }) };
};
export const count = () => ({ type: 'count' });

// ---------- Escrituras ----------
const toWireData = (ref, data) => encode(ref.converter?.toFirestore ? ref.converter.toFirestore(data) : data);

function updateData(args) {
  const [first, ...rest] = args;
  if (typeof first === 'string' || first instanceof FieldPath) {
    const out = {};
    const all = [first, ...rest];
    for (let i = 0; i + 1 < all.length; i += 2) out[fieldName(all[i])] = all[i + 1];
    return out;
  }
  return first;
}

const commitWrites = (writes) => apiCall('/fs/commit', { writes });

export async function setDoc(ref, data, options = {}) {
  await commitWrites([{ type: 'set', path: ref.path, data: toWireData(ref, data), merge: !!options.merge, mergeFields: options.mergeFields?.map(fieldName) }]);
}
export async function updateDoc(ref, ...args) {
  await commitWrites([{ type: 'update', path: ref.path, data: encode(updateData(args)) }]);
}
export async function deleteDoc(ref) {
  await commitWrites([{ type: 'delete', path: ref.path }]);
}
export async function addDoc(colRef, data) {
  const ref = doc(colRef);
  ref.converter = colRef.converter;
  await commitWrites([{ type: 'create', path: ref.path, data: toWireData(ref, data) }]);
  return ref;
}

class WriteBatch {
  constructor() { this._writes = []; this._done = false; }
  set(ref, data, options = {}) {
    this._writes.push({ type: 'set', path: ref.path, data: toWireData(ref, data), merge: !!options.merge, mergeFields: options.mergeFields?.map(fieldName) });
    return this;
  }
  update(ref, ...args) { this._writes.push({ type: 'update', path: ref.path, data: encode(updateData(args)) }); return this; }
  delete(ref) { this._writes.push({ type: 'delete', path: ref.path }); return this; }
  async commit() {
    if (this._done) throw new FirebaseError('failed-precondition', 'Este lote ya se ha enviado');
    this._done = true;
    if (this._writes.length) await commitWrites(this._writes);
  }
}
export function writeBatch() { return new WriteBatch(); }

class Transaction {
  constructor() { this._reads = new Map(); this._writes = []; }
  async get(ref) {
    const snap = await getDoc(ref);
    this._reads.set(ref.path, snap._version);
    return snap;
  }
  set(ref, data, options = {}) {
    this._writes.push({ type: 'set', path: ref.path, data: toWireData(ref, data), merge: !!options.merge });
    return this;
  }
  update(ref, ...args) { this._writes.push({ type: 'update', path: ref.path, data: encode(updateData(args)) }); return this; }
  delete(ref) { this._writes.push({ type: 'delete', path: ref.path }); return this; }
}

export async function runTransaction(db, fn, options = {}) {
  const maxAttempts = options.maxAttempts || 5;
  for (let attempt = 1; ; attempt++) {
    const tx = new Transaction();
    const result = await fn(tx);
    // Precondiciones: los documentos leídos no deben haber cambiado
    const writes = tx._writes.map(w => ({ ...w }));
    for (const [path, version] of tx._reads) {
      const w = writes.find(x => x.path === path);
      if (w) w.precondition = { version };
      else writes.unshift({ type: 'verify', path, precondition: { version } });
    }
    try {
      const real = writes.filter(w => w.type !== 'verify');
      // Las lecturas sin escritura se comprueban con una actualización vacía no hace falta:
      // basta con las precondiciones de los documentos escritos.
      if (real.length) await commitWrites(real);
      return result;
    } catch (e) {
      if (e.code === 'aborted' && attempt < maxAttempts) {
        await new Promise(r => setTimeout(r, 100 * attempt + Math.random() * 100));
        continue;
      }
      throw e;
    }
  }
}

// ---------- Valores especiales ----------
export const serverTimestamp = () => new FieldValueSentinel('serverTimestamp');
export const deleteField = () => new FieldValueSentinel('delete');
export const increment = (n) => new FieldValueSentinel('increment', n);
export const arrayUnion = (...els) => new FieldValueSentinel('arrayUnion', els);
export const arrayRemove = (...els) => new FieldValueSentinel('arrayRemove', els);

// ---------- Tiempo "real" (consulta periódica) ----------
const POLL_MS = 15_000;

export function onSnapshot(target, ...args) {
  let opts = {};
  if (args[0] && typeof args[0] === 'object' && !('next' in args[0]) && typeof args[0] !== 'function') opts = args.shift();
  let onNext, onError;
  if (typeof args[0] === 'object' && args[0] !== null) ({ next: onNext, error: onError } = args[0]);
  else [onNext, onError] = args;
  void opts;

  let stopped = false;
  let timer = null;
  let lastKey = null;
  let prevDocs = null;

  const tick = async () => {
    if (stopped) return;
    try {
      if (target.type === 'document') {
        const snap = await getDoc(target);
        const key = `${snap.exists()}:${snap._version}`;
        if (!stopped && key !== lastKey) { lastKey = key; onNext?.(snap); }
      } else {
        const snap = await getDocs(target);
        const key = snap.docs.map(d => `${d.id}:${d._version}`).join('|');
        if (!stopped && key !== lastKey) {
          lastKey = key;
          const withPrev = new QuerySnapshot(snap.query, snap.docs, prevDocs);
          prevDocs = snap.docs;
          onNext?.(withPrev);
        }
      }
    } catch (e) {
      if (!stopped) onError ? onError(e) : console.warn('onSnapshot:', e);
    }
    if (!stopped) timer = setTimeout(tick, document.hidden ? POLL_MS * 4 : POLL_MS);
  };
  const onVisible = () => { if (!document.hidden) { clearTimeout(timer); tick(); } };
  document.addEventListener('visibilitychange', onVisible);
  tick();
  return () => {
    stopped = true;
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
}

export function onSnapshotsInSync() { return () => {}; }

export { DocumentReference, CollectionReference, Query, DocumentSnapshot, QueryDocumentSnapshot, QuerySnapshot, WriteBatch, Transaction };
